import { type FC } from 'react'
import { type UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { Heading } from 'base-ui'

import { type MarketplacePartnerRegisterPageFormV3Values } from '../../../hooks/marketplacePartnerRegisterPageFormV3RHFModel'
import { type MarketplacePartnerRegisterPageDictionaryOption } from '../../../hooks/useMarketplacePartnerRegisterPageGetDictionaryOptionsByName'

import { MarketplacePartnerRegisterPageFormV3InputField } from './MarketplacePartnerRegisterPageFormV3InputField'
import { MarketplacePartnerRegisterPageFormV3SelectField } from './MarketplacePartnerRegisterPageFormV3SelectField'

type MarketplacePartnerRegisterPageFormV3BusinessDetailsProps = {
  accountTypeOptions?: MarketplacePartnerRegisterPageDictionaryOption[]
  annualRevenueOptions?: MarketplacePartnerRegisterPageDictionaryOption[]
  form: UseFormReturn<MarketplacePartnerRegisterPageFormV3Values>
  marketReadinessOptions?: MarketplacePartnerRegisterPageDictionaryOption[]
  revenueExpectedOptions?: MarketplacePartnerRegisterPageDictionaryOption[]
}

const emptyOptions: MarketplacePartnerRegisterPageDictionaryOption[] = []

export const MarketplacePartnerRegisterPageFormV3BusinessDetails: FC<
  MarketplacePartnerRegisterPageFormV3BusinessDetailsProps
> = ({
  accountTypeOptions,
  annualRevenueOptions,
  form,
  marketReadinessOptions,
  revenueExpectedOptions,
}) => {
  const intl = useIntl()
  const missingError = intl.formatMessage({ id: 'form.error.input.missing' })

  const resolvedAccountTypeOptions = accountTypeOptions ?? emptyOptions
  const resolvedAnnualRevenueOptions = annualRevenueOptions ?? emptyOptions
  const resolvedMarketReadinessOptions = marketReadinessOptions ?? emptyOptions
  const resolvedRevenueExpectedOptions = revenueExpectedOptions ?? emptyOptions

  return (
    <div className="flex flex-col gap-10">
      <Heading level={2} size="l">
        <FormattedMessage id="marketplacePartnerRegisterPage.v3.businessDetails.title" />
      </Heading>
      <div className="flex flex-col gap-4">
        <MarketplacePartnerRegisterPageFormV3SelectField
          fieldName="marketReadiness"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.v3.marketReadiness.label" />
          }
          optionEntries={resolvedMarketReadinessOptions.map((option) => ({
            label: option.label,
            value: option.key,
          }))}
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3SelectField
          fieldName="accountType"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.accountType.label" />
          }
          optionEntries={resolvedAccountTypeOptions.map((option) => ({
            label: option.label,
            value: option.key,
          }))}
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="keyBrand"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.v3.keyBrand.label" />
          }
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3SelectField
          fieldName="annualRevenue"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.v3.annualRevenue.label" />
          }
          optionEntries={resolvedAnnualRevenueOptions.map((option) => ({
            label: option.label,
            value: option.key,
          }))}
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3SelectField
          fieldName="revenueExpected"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.v3.revenueExpected.label" />
          }
          optionEntries={resolvedRevenueExpectedOptions.map((option) => ({
            label: option.label,
            value: option.key,
          }))}
          required
          rules={{ required: missingError }}
        />
      </div>
    </div>
  )
}
